import { z } from "zod";
import { baseEntitySchema } from "./base";

export const paymentAppliesToTypeSchema = z.enum(["po", "invoice"]);
export type PaymentAppliesToType = z.infer<typeof paymentAppliesToTypeSchema>;

export const paymentMethodSchema = z.enum([
  "paypal_goods_services",
  "paypal_friends_family",
  "paypal_goods_services_fees",
  "bank",
  "zelle",
  "card",
  "crypto",
  "other",
  // Legacy value kept for backward compatibility
  "paypal",
]);
export type PaymentMethodType = z.infer<typeof paymentMethodSchema>;

export const paymentStatusSchema = z.enum([
  "pending",
  "cleared",
  "failed",
  "reversed",
  // Saved by an external sync but needs admin sign-off before it's considered
  // confirmed (e.g. a PayPal payment whose amount differs from the PO total).
  "review",
  // A reviewed sync record an admin rejected (e.g. a wrong near-match). Kept so
  // the sync's referenceId dedup never re-creates it; excluded from finance.
  "rejected",
]);
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

// "outgoing" = money paid to the vendor; "incoming" = money the vendor paid back
// (a refund / buyback). Absent is treated as "outgoing" for backward compatibility.
export const paymentDirectionSchema = z.enum(["outgoing", "incoming"]);
export type PaymentDirection = z.infer<typeof paymentDirectionSchema>;

// Provenance of a payment record. A `source` means the record was pulled in from
// an external read-only integration (e.g. the PayPal transaction sync) purely as
// context — it is NOT an authoritative finance entry and must be excluded from
// every cleared/paid total. Absent = a normal, ERP-recorded payment that counts.
export const paymentSourceSchema = z.enum(["paypal_sync"]);
export type PaymentSource = z.infer<typeof paymentSourceSchema>;

export const paymentDataSchema = z.object({
  appliesToType: paymentAppliesToTypeSchema,
  appliesToId: z.string(),
  amount: z.number().nonnegative(),
  method: paymentMethodSchema,
  status: paymentStatusSchema,
  direction: paymentDirectionSchema.optional(),
  source: paymentSourceSchema.optional(),
  // For "review" sync records: the relative amount difference vs the PO total
  // (0–0.15), so the UI can show by how much the amount missed.
  mismatchPct: z.number().optional(),
  paidAt: z.date().optional(),
  referenceId: z.string().optional(),
  notes: z.string().optional(),
});

export type PaymentData = z.infer<typeof paymentDataSchema>;
export const paymentSchema = baseEntitySchema.merge(paymentDataSchema);
export type Payment = z.infer<typeof paymentSchema>;
